// context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';

interface SocialMedia {
  facebook?: string | null;
  instagram?: string | null;
  linkedin?: string | null;
}

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'user' | 'formateur' | 'admin';
  emailConfirmed: boolean;
  provider?: 'local' | 'google';
  photo?: string;
  phone?: string | null;
  address?: string | null;
  bio?: string | null;
  createdAt?: string;
  socialMedia?: SocialMedia | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signup: (userData: SignupData) => Promise<AuthResult>;
  signin: (credentials: SigninData) => Promise<AuthResult>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isUser: boolean;
  isFormateur: boolean;
}

interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface SigninData {
  email: string;
  password: string;
}

interface AuthResult {
  success: boolean;
  error?: string;
  user?: User;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = (): void => {
    setError(null);
  };

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async (): Promise<void> => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (token) {
        const response = await authAPI.getCurrentUser();

        if (response.data && response.data.user) {
          setUser(response.data.user);
          setError(null);
        } else {
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error: any) {
      console.error('AuthContext: Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
      // Don't set error here as this is just a check
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>): void => {
    setUser(prevUser => {
      if (!prevUser) return null;
      return { ...prevUser, ...userData };
    });
  };

  const signup = async (userData: SignupData): Promise<AuthResult> => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.signup(userData);

      if (!response.data || !response.data.token || !response.data.user) {
        throw new Error('Invalid response from server');
      }

      const { token, user: newUser } = response.data;

      localStorage.setItem('token', token);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (error: any) {
      console.error('AuthContext: Signup error:', error);
      const message = error.response?.data?.error || error.message || 'Erreur lors de l\'inscription';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const signin = async (credentials: SigninData): Promise<AuthResult> => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.signin(credentials);

      if (!response.data || !response.data.token || !response.data.user) {
        console.error('AuthContext: Invalid response structure:', response);
        throw new Error('Invalid response from server');
      }

      const { token, user: loggedInUser } = response.data;

      // Store token and set user state
      localStorage.setItem('token', token);
      setUser(loggedInUser);

      return { success: true, user: loggedInUser };
    } catch (error: any) {
      console.error('AuthContext: Signin error:', error);
      const message = error.response?.data?.error || error.message || 'Erreur lors de la connexion';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setError(null);
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signup,
    signin,
    logout,
    checkAuthStatus,
    updateUser,
    clearError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isUser: user?.role === 'user',
    isFormateur: user?.role === 'formateur',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
